import { ButtonHTMLAttributes, ReactNode, useEffect, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { cn } from '../../lib/utils';
import { LoadingSpinner } from './LoadingSpinner';

/** Reserve geometry immediately; reveal decoration only when a wait is perceptible. */
function useLoadingDelay(pending = true) {
  const [visible, setVisible] = useState(false);
  const [hidden, setHidden] = useState(document.hidden);
  useEffect(() => {
    setVisible(false);
    if (!pending) return;
    const timer = window.setTimeout(() => setVisible(true), 150);
    const onVisibility = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pending]);
  return { visible: pending && visible, hidden };
}

export function LoadingStatus({ label, compact = false }: { label?: string; compact?: boolean }) {
  const { t } = useI18n();
  const { visible, hidden } = useLoadingDelay();
  return (
    <span
      role="status"
      data-loading-paused={hidden || undefined}
      className={cn(
        'inline-flex min-h-6 items-center gap-2 text-sm text-slate-600',
        !visible && 'invisible'
      )}
    >
      <LoadingSpinner size="sm" />
      <span className={compact ? 'sr-only' : undefined}>{label || t('common.loading')}</span>
    </span>
  );
}

export function LoadingRegion({
  shape = 'list',
  label,
}: {
  shape?: 'list' | 'weeks' | 'calendar' | 'form';
  label?: string;
}) {
  const { t } = useI18n();
  const { visible, hidden } = useLoadingDelay();
  return (
    <div className="space-y-3" data-loading-shape={shape} data-loading-paused={hidden || undefined}>
      <span role="status" className="sr-only">
        {label || t('common.loading')}
      </span>
      <div aria-busy="true" className={cn('loading-decoration', !visible && 'opacity-0')}>
        <div
          aria-hidden="true"
          className={shape === 'calendar' ? 'grid grid-cols-7 gap-2' : 'space-y-3'}
        >
          {Array.from({ length: shape === 'calendar' ? 35 : 3 }, (_, index) => (
            <div
              key={index}
              className={cn(
                'rounded-lg bg-slate-100 p-4',
                shape === 'weeks' ? 'h-24' : shape === 'calendar' ? 'h-12 sm:h-20' : 'h-16'
              )}
            >
              <div className="h-3 w-2/3 rounded bg-slate-200" />
              {shape !== 'calendar' && <div className="mt-3 h-2 w-1/3 rounded bg-slate-200" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function RefreshStatus() {
  const { t } = useI18n();
  return <LoadingStatus label={t('loading.refreshing')} />;
}

export function BusyButton({
  busy,
  busyLabel,
  children,
  className,
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  busy: boolean;
  busyLabel: string;
  children: ReactNode;
}) {
  return (
    <button
      {...props}
      disabled={disabled || busy}
      aria-busy={busy}
      className={cn(className, 'inline-grid items-center justify-items-center')}
    >
      <span
        className={cn(
          'col-start-1 row-start-1 inline-flex items-center gap-2',
          busy && 'invisible'
        )}
        aria-hidden={busy || undefined}
      >
        {children}
      </span>
      <span
        className={cn(
          'col-start-1 row-start-1 inline-flex items-center gap-2',
          !busy && 'invisible'
        )}
        aria-hidden={!busy || undefined}
      >
        <LoadingSpinner size="sm" />
        {busyLabel}
      </span>
    </button>
  );
}
